import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import Logo from "./Logo";
import { CONTACT, waHref, telHref, mailHref } from "@/lib/contact";
import { CTA } from "@/lib/business";
import { trackEvent } from "@/lib/analytics";

const onPhoneClick = () => {
  trackEvent("phone_click", { location: "footer" });
  if (typeof window !== "undefined" && window.location.pathname === "/") {
    trackEvent("homepage_phone_click", { location: "footer" });
  }
};

const INSPECTION_LINKS = [
  { label: "Snagging", href: "/property-snagging-dubai" },
  { label: "Handover", href: "/handover-inspection-dubai" },
  { label: "Villa", href: "/villa-inspection-dubai" },
  { label: "Apartment", href: "/apartment-inspection-dubai" },
  { label: "Resale", href: "/resale-property-inspection-dubai" },
  { label: "Warranty / DLP", href: "/dlp-warranty-inspection-dubai" },
  { label: "Commercial", href: "/commercial-property-inspection-dubai" },
  { label: "Reinspection", href: "/reinspection-dubai" },
  { label: "UAE-wide inspections", href: "/property-inspection-uae" },
  { label: "Guides", href: "/guides" },
  { label: "Inspection Tools", href: "/inspection-tools" },
  { label: "Partners", href: "/partners" },
];

const LOCATION_LINKS = [
  { label: "Dubai", href: "/property-snagging-dubai" },
  { label: "Abu Dhabi", href: "/property-inspection-abu-dhabi" },
  { label: "Sharjah", href: "/property-inspection-sharjah" },
  { label: "Dubai communities", href: "/property-snagging-dubai-communities" },
];

const LEGAL_LINKS = [
  { label: "Cancellation & Refund", href: "/cancellation-refund-policy" },
  { label: "Scope & Exclusions", href: "/inspection-scope-exclusions" },
  { label: "Terms & Conditions", href: "/terms-and-conditions" },
  { label: "الشروط والأحكام", href: "/ar/terms-and-conditions" },
  { label: "Privacy", href: "/privacy-policy" },
  { label: "Liability", href: "/limitation-of-liability" },
  { label: "Cookie settings", action: "cookie-settings" },
];

export default function Footer({ onBook, onQuote }) {
  return (
    <>
      {/* Contact */}
      <section id="contact" className="py-24 lg:py-28 bg-navy">
        <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Logo variant="white" className="mb-6" />
            <span className="eyebrow">Get in touch</span>
            <h2 className="text-[clamp(1.85rem,3.4vw,2.5rem)] mt-3 mb-4 text-white">Get your quote — we reply quickly.</h2>
            <p className="text-[#a7b2c4] max-w-[42ch] mb-8">Tell us about your property and we'll send an exact, no-obligation quote. Prefer to talk? Message a real inspector on WhatsApp now.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => onQuote()} data-testid="footer-quote-btn" className="inline-flex items-center gap-2 bg-blue text-white px-7 py-4 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">{CTA.primary}</button>
              <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-btn" className="inline-flex items-center gap-2 border border-[#232A34] text-white px-7 py-4 rounded-[10px] font-semibold hover:border-blue transition-colors"><MessageCircle size={18} /> WhatsApp Fedari</a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-3">
              {[[Phone, "PHONE", CONTACT.phoneDisplay, telHref], [MessageCircle, "WHATSAPP", "Message an inspector", waHref], [Mail, "EMAIL", CONTACT.email, mailHref]].map(([Icon, label, val, href]) => (
                <a key={label} href={href} target={label === "WHATSAPP" ? "_blank" : undefined} rel="noopener noreferrer"
                  onClick={label === "PHONE" ? onPhoneClick : undefined}
                  data-testid={label === "PHONE" ? "footer-phone-link" : undefined}
                  className="flex items-center gap-4 bg-[#161B22] border border-[#232A34] rounded-[14px] p-5 hover:border-blue transition-colors">
                  <span className="w-11 h-11 rounded-[11px] bg-blue-50/10 border border-[#232A34] grid place-items-center text-blue flex-none"><Icon size={18} /></span>
                  <div><div className="font-mono-jb text-[0.66rem] tracking-wider text-[#8fa0bd]">{label}</div><div className="text-white font-semibold">{val}</div></div>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="bg-soft border-t border-line pt-16 pb-7">
        <div className="wrap">
          <div className="grid md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-8 mb-11">
            <div>
              <Logo />
              <p className="text-muted text-[0.88rem] max-w-[32ch] mt-4">Independent property inspection and snagging specialists in Dubai and selected UAE locations, subject to confirmed availability.</p>
            </div>
            <FootCol title="Inspections" links={INSPECTION_LINKS} />
            <FootCol title="Locations" links={LOCATION_LINKS} />
            <div>
              <h2 className="font-mono-jb text-[0.68rem] tracking-wider uppercase text-ink mb-3.5">Contact</h2>
              <address className="text-muted text-[0.89rem] space-y-1.5 not-italic">
                <a href={telHref} onClick={onPhoneClick} data-testid="footer-contact-phone" className="block hover:text-blue">{CONTACT.phoneDisplay}</a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="block hover:text-blue">WhatsApp</a>
                <a href={mailHref} className="block hover:text-blue">{CONTACT.email}</a>
                <Link to="/contact" className="block hover:text-blue">Contact page</Link>
                <div className="pt-1 text-[0.84rem] leading-relaxed" data-testid="footer-address">
                  {CONTACT.addressLines.map((l) => <div key={l}>{l}</div>)}
                  <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-1.5 text-blue font-semibold hover:underline" data-testid="footer-maps-link">
                    <MapPin size={13} /> Open location in Google Maps
                  </a>
                </div>
                <div className="text-[0.78rem] text-muted pt-1">{LEGAL_ENTITY}</div>
                <a href="https://www.nachi.org/verify?nachiid=NACHI26090103" target="_blank" rel="noopener noreferrer" className="inline-block mt-4 p-2" aria-label="Verify Fedari's InterNACHI certification" data-testid="footer-internachi-logo">
                  <img src="/images/internachi-logo-256.webp" alt="InterNACHI — International Association of Certified Home Inspectors" width="256" height="256" className="w-12 h-12" loading="lazy" decoding="async" />
                </a>
              </address>
            </div>
          </div>
          <div className="border-t border-line pt-6 flex flex-wrap justify-between gap-3 text-[0.8rem] text-muted">
            <span>© {new Date().getFullYear()} Fedari Inspection · Trade Licence 1647070. All rights reserved.</span>
            <nav className="flex flex-wrap gap-x-1 gap-y-2" aria-label="Legal">
              {LEGAL_LINKS.map((l, i) => (
                <span key={l.href || l.action} className="inline-flex items-center">
                  {l.action ? (
                    <button type="button" data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new CustomEvent("fedari:cookie-settings"))} className="hover:text-blue py-1.5">{l.label}</button>
                  ) : (
                    <Link to={l.href} className="hover:text-blue py-1.5">{l.label}</Link>
                  )}
                  {i < LEGAL_LINKS.length - 1 && <span className="mx-1.5">·</span>}
                </span>
              ))}
            </nav>
          </div>
        </div>
      </footer>
      {/* Spacer so the fixed mobile action bar never covers the footer content */}
      <div className="h-[78px] lg:hidden" aria-hidden="true" />
    </>
  );
}

const LEGAL_ENTITY = "FEDARI BUILDING INSPECTION L.L.C";

function FootCol({ title, links }) {
  return (
    <div>
      <h2 className="font-mono-jb text-[0.68rem] tracking-wider uppercase text-ink mb-3.5">{title}</h2>
      {links.map((l) => <Link key={l.href} to={l.href} data-testid={l.href === "/partners" ? "footer-partners-link" : undefined} className="block text-muted text-[0.89rem] py-1 hover:text-blue transition-colors">{l.label}</Link>)}
    </div>
  );
}
